/* eslint-disable sonarjs/cognitive-complexity */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { getLookupData } from 'modules/api/lookups';
import multiPublisherRequest from 'modules/api/multiPublisherRequest';
import withCancelRequest from 'components/hocs/withCancelRequest';
import { SOURCE_SYSTEM } from 'consts/sourceSystem';
import { cancelFunctions } from 'utils/cancelFunctions';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import Chip, { Color, Width } from 'lib/Chip';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import Button, { Color as ButtonColor } from 'lib/Button';
import { Label } from 'lib/Label';
import AsyncSelect from 'lib/AsyncSelect';
import { getIsReseller } from 'store/publisher/selectors';
import { AsyncSelectLoadDataHandler, SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { getResellerDealMediaOwners } from 'store/reseller/selectors';
import { RootState } from 'store';
import { CodeNameModel, FileList } from 'components/common/types/index';
import { updateFrameSelection } from 'utils/frameList';
import ListUpload from './ListUpload';
import InfoMessage from '../InfoMessage';

type ListProps = {
  isMenuClosedOnSelect?: boolean;
};

type RouteFrameCodeWithLabel = RouteFrameCode & { label: string };

const List = ({ isMenuClosedOnSelect = true }: ListProps): React.ReactElement => {
  const dispatch = useDispatch();
  const resellerMediaOwners = useSelector(getResellerDealMediaOwners);
  const isReseller = useSelector(getIsReseller);
  const environmentId = useSelector((state: RootState) => state.environment.environmentId);
  const sourceSystem = useSelector((state: Store) => state.dealManagement.commonDeal.sourceSystem);
  const routeFrameCodes = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.routeFrameCodes);
  const listFiles = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.listFiles);

  const readOnly = useSelector(getDealFormReadOnly);
  const isSourceSystemAutomation = sourceSystem === SOURCE_SYSTEM.AUTOMATION;
  const [routeFrameCodesWithLabels, setRouteFrameCodesWithLabels] = useState<RouteFrameCodeWithLabel[] | null>([]);

  const fetchRouteFrameCodes: AsyncSelectLoadDataHandler<RouteFrameCodeWithLabel | []> = async (
    dropdownName: string,
    lookupCode: string,
  ) => {
    const requestArguments = { cancelFunctions, lookupURLName: dropdownName, query: '', lookupCode };
    try {
      if (isReseller) {
        const response = (await multiPublisherRequest(environmentId, resellerMediaOwners, getLookupData, {
          ...requestArguments,
        })) as RouteFrameCodeWithLabel[];
        return response.map(({ publisher, code, ...rest }) => ({
          ...rest,
          code,
          mediaOwner: publisher,
          label: `${publisher} : ${code}`,
        }));
      }

      const response: RouteFrameCodeWithLabel[] = await getLookupData({ ...requestArguments });
      return response.map(({ code, ...rest }) => ({ ...rest, code, mediaOwner: environmentId, label: code }));
    } catch {
      return [];
    }
  };

  const handleFrameListUpload = (newListFiles: FileList[]): void => {
    dispatch(changeDealCurrentLineData({ listFiles: newListFiles }));
  };

  useEffect(() => {
    setRouteFrameCodesWithLabels(
      routeFrameCodes?.map((routeFrame) => ({
        ...routeFrame,
        label: isReseller ? `${routeFrame.mediaOwner} : ${routeFrame.code}` : routeFrame.code,
      })) || [],
    );
  }, [routeFrameCodes]);

  const onIncludeExcludeChange = useCallback(
    (value: CodeNameModel): void => {
      if (!routeFrameCodes) return;

      const frameCodes = structuredClone(routeFrameCodes);
      const updatedFrameCodes = updateFrameSelection(frameCodes, value);

      dispatch(changeDealCurrentLineData({ routeFrameCodes: updatedFrameCodes }));
    },
    [routeFrameCodes],
  );

  const onRemoveItem = useCallback(
    (code: string) => {
      const newFrameSelection = routeFrameCodes?.filter((frameCode) => frameCode.code !== code);
      dispatch(changeDealCurrentLineData({ routeFrameCodes: newFrameSelection }));
    },
    [routeFrameCodes],
  );

  const onSelect: SelectHandler<RouteFrameCodeWithLabel> = (_, value: RouteFrameCodeWithLabel[]): void => {
    const newFrameSelection = value.map((item: RouteFrameCodeWithLabel) => ({
      ...item,
      include: item.include !== false,
    }));

    dispatch(changeDealCurrentLineData({ routeFrameCodes: newFrameSelection }));
  };

  return (
    <div className="grid grid-cols-2 gap-x-7 xl:gap-x-24 px-12 py-6" data-test-id="list">
      <div>
        <Label label="Frames" />
        <div className="mb-1.5">
          <AsyncSelect
            dataTestId="routeframecode-input"
            name="routeframecode"
            loadData={fetchRouteFrameCodes}
            selectedValue={routeFrameCodesWithLabels}
            placeholder="Search route frame"
            onSelect={onSelect}
            isClearable={false}
            multiple
            isMultiValueVisible={false}
            closeMenuOnSelect={isMenuClosedOnSelect}
            valueKey="code"
            textKey="label"
            isDisabled={readOnly || isSourceSystemAutomation}
          />
          {isSourceSystemAutomation && (
            <div className="mt-4">
              <InfoMessage>
                To find the list of frames targeted, please refer to Frame list in the summary panel.
              </InfoMessage>
            </div>
          )}
        </div>
        {!isSourceSystemAutomation && routeFrameCodes?.length ? (
          <div data-test-id="list-chips" className="flex flex-col gap-2 mt-2">
            {routeFrameCodesWithLabels?.map((item) => (
              <div key={item.code} aria-label={item.label} className="flex justify-between items-center">
                <Chip
                  label={item.label}
                  color={Color.PRIMARY}
                  width={Width.FULL}
                  truncateLabel={false}
                  isDisabled={readOnly}
                  onRemove={() => onRemoveItem(item.code)}
                />
                <IncludeExcludeToggle
                  option={item}
                  onIncludeExcludeChange={onIncludeExcludeChange}
                  isAlwaysVisible
                  isSelected={item.include !== undefined}
                  isOptionNameSelectable={false}
                  isHovered={false}
                  isDeselectAllowed={false}
                  isDisabled={readOnly}
                />
              </div>
            ))}
            {!readOnly && (
              <Button
                classNames="w-fit"
                label="Clear"
                color={ButtonColor.SECONDARY}
                onClick={() => dispatch(changeDealCurrentLineData({ routeFrameCodes: [] }))}
              />
            )}
          </div>
        ) : null}
      </div>
      <ListUpload isReadOnly={readOnly} listFiles={listFiles} onChangeListFiles={handleFrameListUpload} />
    </div>
  );
};

export default withCancelRequest(List);
/* eslint-enable sonarjs/cognitive-complexity */
